function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip, ChartArea } from "@progress/kendo-react-charts";

/*
 * Telerik chart adapter behind the legacy google-charts wrapper API.
 *
 * Consumers keep passing {chartType, data, options, width, height} in
 * react-google-charts shape (data = [header, ...rows]; options = the
 * google subset this app actually uses) and get Kendo charts — the whole
 * Territory/Dashboard/Keywords family switched libraries in this one file
 * (user-directed 2026-07-30, "switch charts to telerik where possible").
 * The GeoChart choropleth (components/geo-choropleth) has no Kendo
 * equivalent and stays on react-google-charts.
 *
 * Supported mapping: ColumnChart · LineChart · ComboChart (seriesType +
 * per-series {type, color, targetAxisIndex, lineDashStyle}); vAxis/vAxes
 * title, format:"short"/"0", viewWindow.min/minValue, direction:-1 →
 * reverse (the Keywords rank axis); hAxis.showTextEvery → label step;
 * legend.position (none/top/bottom); colors; pointSize → line markers;
 * missing values render as gaps (interpolateNulls was never true here);
 * {role:"interval"} column pairs → a translucent rangeArea band under the
 * preceding series (the forecast ±1.96σ band).
 */

// Nice tick step (1, 2, 2.5, 5 x 10^n) that splits `span` into about `target` parts.
function niceStep(span, target = 5) {
  if (!(span > 0)) return 0;
  const raw = span / target;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / pow;
  const m = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return m * pow;
}

// Decimals needed so neighbouring ticks `step` apart never print the same text
// once divided by `unit` (1, 1e3 or 1e6). Rounding to whole K used to print
// "4K" on every gridline of a 3.6K-4.4K axis, and "1K, 1K" on a stock line.
function decimalsFor(step, unit) {
  if (!(step > 0)) return 0;
  const s = step / unit;
  let d = 0;
  while (d < 3 && Math.abs(Math.round(s * Math.pow(10, d)) - s * Math.pow(10, d)) > 1e-6) d++;
  return d;
}

// For charts that use Kendo directly: {min, majorUnit} for an all-positive
// count axis so small ranges step in whole numbers instead of 0.5s that a
// "{0:n0}" label prints twice (0, 1, 1, 2, 2).
export function countAxis(values) {
  const vals = (values || []).map(Number).filter(v => isFinite(v));
  const hi = vals.length ? Math.max(...vals) : 0;
  return {
    min: 0,
    majorUnit: Math.max(1, Math.round(niceStep(hi > 0 ? hi : 1)))
  };
}
function shortLabel(v, step) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const fmt = (x, unit, suffix) => {
    const d = step ? decimalsFor(step, unit) : unit === 1 ? 2 : unit === 1e6 ? 1 : 0;
    const r = Number((x / unit).toFixed(d));
    return `${r.toLocaleString("en-US", {
      maximumFractionDigits: d
    })}${suffix}`;
  };
  if (abs >= 1e6) return fmt(n, 1e6, "M");
  if (abs >= 1e3) return fmt(n, 1e3, "K");
  return fmt(n, 1, "");
}
export default class extends React.Component {
  parse() {
    const {
      data,
      options = {},
      chartType
    } = this.props;
    const header = data[0] || [];
    const rows = data.slice(1);

    // Column classification: col 0 = category; {role:"interval"} pairs
    // band the nearest preceding value column; the rest are series.
    const seriesCols = []; // {index, name}
    const bands = {}; // value-col index -> {lowerCol, upperCol}
    let lastValueCol = null;
    for (let c = 1; c < header.length; c++) {
      const h = header[c];
      if (h && typeof h === "object" && h.role === "interval") {
        if (lastValueCol != null) {
          const b = bands[lastValueCol] = bands[lastValueCol] || {};
          if (b.lowerCol == null) b.lowerCol = c;else b.upperCol = c;
        }
      } else if (h && typeof h === "object" && h.role) {
        // Other google roles (annotation, tooltip, style) are not series;
        // treating them as one drew a NaN "[object Object]" series.
        continue;
      } else {
        seriesCols.push({
          index: c,
          name: String(h)
        });
        lastValueCol = c;
      }
    }
    const categories = rows.map(r => String(r[0]));
    const defaultType = chartType === "LineChart" ? "line" : chartType === "BarChart" ? "bar" : "column";
    const optSeries = options.series || {};
    const colors = options.colors || [];
    const series = seriesCols.map((col, i) => {
      const o = optSeries[i] || {};
      const band = bands[col.index];
      return {
        name: col.name,
        type: o.type || defaultType,
        color: o.color || colors[i],
        axis: o.targetAxisIndex === 1 ? "axis1" : "axis0",
        dash: !!o.lineDashStyle,
        tooltipFormat: o.tooltipFormat,
        data: rows.map(r => r[col.index] == null ? null : Number(r[col.index])),
        band: band && band.upperCol != null ? rows.map(r => ({
          from: r[band.lowerCol] == null ? null : Number(r[band.lowerCol]),
          to: r[band.upperCol] == null ? null : Number(r[band.upperCol])
        })) : null
      };
    });
    return {
      categories,
      series,
      usesAxis1: series.some(s => s.axis === "axis1")
    };
  }

  // Scale for one value axis, computed from the data so labels never repeat:
  // counts (format "0") step in whole numbers (no "1,1,1,0,0" on a 0-1.4
  // units line), every axis gets a 1/2/2.5/5 step, and an all-positive axis
  // starts at 0 so a single point (one stock snapshot) is not drawn on a
  // zoomed-in 1,180-1,240 range.
  axisScale(axisName, axisOpts = {}, series = []) {
    const vals = [];
    series.forEach(s => {
      if (s.axis !== axisName) return;
      s.data.forEach(v => {
        if (v != null && isFinite(v)) vals.push(v);
      });
      if (s.band) s.band.forEach(b => {
        if (b.from != null) vals.push(b.from);
        if (b.to != null) vals.push(b.to);
      });
    });
    const vwMin = axisOpts.viewWindow && axisOpts.viewWindow.min != null ? axisOpts.viewWindow.min : axisOpts.minValue != null ? axisOpts.minValue : undefined;
    const integer = axisOpts.format === "0";
    if (!vals.length) return {
      min: vwMin,
      majorUnit: integer ? 1 : undefined
    };
    let lo = Math.min(...vals),
      hi = Math.max(...vals);
    let min = vwMin;
    if (min == null && lo >= 0) min = 0;
    if (min != null) lo = Math.min(lo, min);
    if (hi === lo) hi = lo + (integer ? 1 : Math.max(1, Math.abs(lo)));
    let step = niceStep(hi - lo);
    if (integer) step = Math.max(1, Math.round(step));
    return {
      min,
      majorUnit: step || undefined
    };
  }
  axisLabels(axisOpts = {}, step) {
    const labels = {};
    if (axisOpts.format === "short") labels.content = e => shortLabel(e.value, step);else if (axisOpts.format === "0") labels.content = e => String(Math.round(e.value));
    // Non-google extension: thin value-axis labels on short charts
    // (Kendo won't auto-thin the way google did at ~110px heights).
    if (axisOpts.showTextEvery > 1) labels.step = axisOpts.showTextEvery;
    return labels;
  }
  render() {
    const {
      data,
      options = {},
      width,
      height
    } = this.props;

    // Legacy empty state (kept from the google-charts wrapper).
    if (!data || data.length < 2) {
      return /*#__PURE__*/React.createElement("div", {
        className: "flex-height-container chart"
      }, /*#__PURE__*/React.createElement("div", {
        className: "error-container"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons error-icon"
      }, "report_problem"), /*#__PURE__*/React.createElement("div", {
        className: "error-container-notification"
      }, /*#__PURE__*/React.createElement("span", null, "Oops..."), /*#__PURE__*/React.createElement("span", null, "There currently is no data to render for this chart."))));
    }
    const {
      categories,
      series,
      usesAxis1
    } = this.parse();
    // Google's BarChart is horizontal: its value axis is hAxis.
    const horizontal = this.props.chartType === "BarChart";
    const vAxis = (horizontal ? options.hAxis : options.vAxis || options.vAxes && options.vAxes[0]) || {};
    const vAxis1 = options.vAxes && options.vAxes[1] || {};
    const legendPos = options.legend && options.legend.position;
    const showTextEvery = !horizontal && options.hAxis && options.hAxis.showTextEvery || 1;
    const pointSize = options.pointSize;
    const scale0 = this.axisScale("axis0", vAxis, series);
    const scale1 = usesAxis1 ? this.axisScale("axis1", vAxis1, series) : {};
    // Tooltip number format: 'currency' → $#,##0.00 (the raw values carry
    // float noise like 809668.5700000001); any kendo format string passes
    // through untouched.
    const ttOpt = options.tooltip && options.tooltip.format;
    const tooltipFormat = ttOpt === "currency" ? "{0:c}" : ttOpt;
    return /*#__PURE__*/React.createElement(Chart, {
      style: {
        width: width || "100%",
        height: height || "300px"
      },
      transitions: false
    }, /*#__PURE__*/React.createElement(ChartArea, {
      background: "transparent"
    }), /*#__PURE__*/React.createElement(ChartLegend, {
      position: legendPos === "top" ? "top" : "bottom",
      visible: legendPos !== "none"
    }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
      categories: categories,
      majorGridLines: {
        visible: false
      },
      labels: {
        step: showTextEvery,
        rotation: options.hAxis && options.hAxis.slantedText === false ? 0 : "auto"
      }
      // Second value axis crosses at the far end → renders on the RIGHT
      ,
      axisCrossingValue: usesAxis1 ? [0, categories.length] : undefined
    })), /*#__PURE__*/React.createElement(ChartValueAxis, null, [/*#__PURE__*/React.createElement(ChartValueAxisItem, {
      key: "axis0",
      name: "axis0",
      title: vAxis.title ? {
        text: vAxis.title
      } : undefined,
      labels: this.axisLabels(vAxis, scale0.majorUnit),
      min: scale0.min,
      majorUnit: scale0.majorUnit,
      reverse: vAxis.direction === -1
    }), ...(usesAxis1 ? [/*#__PURE__*/React.createElement(ChartValueAxisItem, {
      key: "axis1",
      name: "axis1",
      title: vAxis1.title ? {
        text: vAxis1.title
      } : undefined,
      labels: this.axisLabels(vAxis1, scale1.majorUnit),
      min: scale1.min,
      majorUnit: scale1.majorUnit
    })] : [])]), /*#__PURE__*/React.createElement(ChartSeries, null, series.reduce((items, s, i) => {
      if (s.band) {
        items.push(/*#__PURE__*/React.createElement(ChartSeriesItem, {
          key: `band-${i}`,
          type: "rangeArea",
          data: s.band,
          fromField: "from",
          toField: "to",
          color: s.color,
          opacity: 0.15,
          axis: "axis0",
          tooltip: {
            visible: false
          },
          visibleInLegend: false
        }));
      }
      // Kendo's tooltip resolver does
      // `series.hasOwnProperty("tooltip") && series.tooltip.hasOwnProperty(...)`
      // — a prop passed as undefined still creates the key and
      // crashes on hover. Only spread props that are defined.
      const extra = {};
      const fmt = s.tooltipFormat === "currency" ? "{0:c}" : s.tooltipFormat || tooltipFormat;
      if (fmt) extra.tooltip = {
        format: fmt
      };
      if (s.dash) extra.dashType = "dash";
      if (s.type === "line") extra.markers = {
        visible: pointSize !== 0,
        size: pointSize || 5
      };
      if (s.color) extra.color = s.color;
      items.push(/*#__PURE__*/React.createElement(ChartSeriesItem, _extends({
        key: i,
        type: s.type,
        name: s.name,
        data: s.data,
        axis: s.axis,
        missingValues: "gap"
      }, extra)));
      return items;
    }, [])), /*#__PURE__*/React.createElement(ChartTooltip, null));
  }
}